const SCROLL_ANIMATION_TRIGGER_CLASSNAME = 'scroll-trigger';
const SCROLL_ANIMATION_OFFSCREEN_CLASSNAME = 'scroll-trigger--offscreen';
const SCROLL_ZOOM_IN_TRIGGER_CLASSNAME = 'animate--zoom-in';
const SCROLL_ANIMATION_CANCEL_CLASSNAME = 'scroll-trigger--cancel';
const GSAP_ACTIVE_CLASSNAME = 'js-gsap';

const FALLBACK_ZOOM_INIT_ATTRIBUTE = 'data-fallback-zoom-initialized';
const GSAP_SCROLL_INIT_ATTRIBUTE = 'data-gsap-scroll-initialized';
const GSAP_ZOOM_INIT_ATTRIBUTE = 'data-gsap-zoom-initialized';

const fallbackState = {
  observers: [],
  zoomCleanups: [],
  zoomInitialized: false,
};

let gsapRegistered = false;
let gsapInitialized = false;
let fallbackZoomTimeout;

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function shouldUseGsapEnhancement() {
  return Boolean(window.themeAnimations?.useGsap);
}

function canUseGsap() {
  return (
    shouldUseGsapEnhancement() &&
    !prefersReducedMotion() &&
    typeof window.gsap !== 'undefined' &&
    typeof window.ScrollTrigger !== 'undefined'
  );
}

function registerGsap() {
  if (!canUseGsap()) return false;
  if (!gsapRegistered) {
    window.gsap.registerPlugin(window.ScrollTrigger);
    gsapRegistered = true;
  }
  return true;
}

function clearFallbackState() {
  fallbackState.observers.forEach((observer) => observer.disconnect());
  fallbackState.observers = [];

  fallbackState.zoomCleanups.forEach((cleanup) => cleanup());
  fallbackState.zoomCleanups = [];
  fallbackState.zoomInitialized = false;
}

function scheduleFallbackZoomInitialization() {
  if (fallbackZoomTimeout) window.clearTimeout(fallbackZoomTimeout);
  fallbackZoomTimeout = window.setTimeout(() => {
    if (!gsapInitialized) initializeScrollZoomAnimationTriggerFallback();
  }, 1000);
}

function clearFallbackZoomTimeout() {
  if (fallbackZoomTimeout) {
    window.clearTimeout(fallbackZoomTimeout);
    fallbackZoomTimeout = undefined;
  }
}

// Scroll in animation fallback logic
function onIntersection(entries, observer) {
  entries.forEach((entry, index) => {
    if (entry.isIntersecting) {
      const entryTarget = entry.target;
      if (entryTarget.classList.contains(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME)) {
        entryTarget.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
        if (entryTarget.hasAttribute('data-cascade')) entryTarget.setAttribute('style', `--animation-order: ${index};`);
      }
      observer.unobserve(entryTarget);
      return;
    }

    entry.target.classList.add(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    entry.target.classList.remove(SCROLL_ANIMATION_CANCEL_CLASSNAME);
  });
}

function initializeScrollAnimationTriggerFallback(rootEl = document, isDesignModeEvent = false) {
  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  if (isDesignModeEvent) {
    animationTriggerElements.forEach((element) => {
      element.classList.add('scroll-trigger--design-mode');
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
    });
    return;
  }

  const observer = new IntersectionObserver(onIntersection, {
    rootMargin: '0px 0px -50px 0px',
  });

  animationTriggerElements.forEach((element) => observer.observe(element));
  fallbackState.observers.push(observer);
}

function throttleWithAnimationFrame(callback) {
  let pending = false;
  return () => {
    if (pending) return;
    pending = true;
    window.requestAnimationFrame(() => {
      pending = false;
      callback();
    });
  };
}

function initializeScrollZoomAnimationTriggerFallback(rootEl = document) {
  if (prefersReducedMotion()) return;

  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  const scaleAmount = 0.2 / 100;

  animationTriggerElements.forEach((element) => {
    if (element.hasAttribute(FALLBACK_ZOOM_INIT_ATTRIBUTE)) return;
    element.setAttribute(FALLBACK_ZOOM_INIT_ATTRIBUTE, 'true');

    let elementIsVisible = false;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        elementIsVisible = entry.isIntersecting;
      });
    });
    observer.observe(element);

    const updateScale = () => {
      if (!elementIsVisible) return;
      element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));
    };

    element.style.setProperty('--zoom-in-ratio', 1 + scaleAmount * percentageSeen(element));

    const onScroll = throttleWithAnimationFrame(updateScale);
    window.addEventListener('scroll', onScroll, { passive: true });

    fallbackState.zoomCleanups.push(() => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      element.removeAttribute(FALLBACK_ZOOM_INIT_ATTRIBUTE);
      element.style.removeProperty('--zoom-in-ratio');
    });
  });

  fallbackState.zoomInitialized = true;
}

function getCascadeDelay(element, cascadeCounter) {
  if (!element.hasAttribute('data-cascade')) return 0;

  const parent = element.parentElement || element;
  const order = cascadeCounter.get(parent) || 0;
  cascadeCounter.set(parent, order + 1);
  return order * 0.075;
}

function getZoomAnimationTarget(element) {
  return Array.from(element.children).find((child) =>
    child.matches('img:not(.zoom):not(.deferred-media__poster-button), .svg-wrapper')
  );
}

function isWithinActiveViewport(element) {
  const rect = element.getBoundingClientRect();
  return rect.top <= window.innerHeight * 0.9 && rect.bottom >= 0;
}

function initializeGsapScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  if (!registerGsap()) return;

  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ANIMATION_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  const cascadeCounter = new Map();

  animationTriggerElements.forEach((element) => {
    if (isDesignModeEvent) {
      element.classList.add('scroll-trigger--design-mode');
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      element.removeAttribute(GSAP_SCROLL_INIT_ATTRIBUTE);
      window.gsap.set(element, { clearProps: 'opacity,visibility,transform,will-change' });
      return;
    }

    if (element.classList.contains(SCROLL_ANIMATION_CANCEL_CLASSNAME)) {
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      return;
    }

    const isFadeAnimation = element.classList.contains('animate--fade-in');
    const isSlideAnimation = element.classList.contains('animate--slide-in');
    if (!isFadeAnimation && !isSlideAnimation) return;

    if (element.getAttribute(GSAP_SCROLL_INIT_ATTRIBUTE) === 'true') return;
    element.setAttribute(GSAP_SCROLL_INIT_ATTRIBUTE, 'true');

    if (isWithinActiveViewport(element)) {
      element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      window.gsap.set(element, { clearProps: 'opacity,visibility,transform,will-change' });
      return;
    }

    window.gsap.set(element, {
      autoAlpha: 0.01,
      y: isSlideAnimation ? 24 : 0,
      force3D: isSlideAnimation,
      willChange: isSlideAnimation ? 'transform,opacity' : 'opacity',
    });

    window.gsap.to(element, {
      autoAlpha: 1,
      y: 0,
      duration: isSlideAnimation ? 0.7 : 0.6,
      ease: 'power2.out',
      delay: getCascadeDelay(element, cascadeCounter),
      overwrite: 'auto',
      onStart: () => {
        element.classList.remove(SCROLL_ANIMATION_OFFSCREEN_CLASSNAME);
      },
      onComplete: () => {
        element.style.removeProperty('will-change');
      },
      scrollTrigger: {
        trigger: element,
        start: 'top 88%',
        once: true,
        fastScrollEnd: true,
      },
    });
  });

  window.ScrollTrigger.refresh();
}

function initializeGsapScrollZoomAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  if (!registerGsap()) return;

  const animationTriggerElements = Array.from(rootEl.getElementsByClassName(SCROLL_ZOOM_IN_TRIGGER_CLASSNAME));
  if (animationTriggerElements.length === 0) return;

  animationTriggerElements.forEach((element) => {
    const zoomTarget = getZoomAnimationTarget(element);
    if (!zoomTarget) return;

    if (isDesignModeEvent) {
      element.removeAttribute(GSAP_ZOOM_INIT_ATTRIBUTE);
      window.gsap.set(zoomTarget, { clearProps: 'transform,will-change' });
      return;
    }

    if (element.getAttribute(GSAP_ZOOM_INIT_ATTRIBUTE) === 'true') return;
    element.setAttribute(GSAP_ZOOM_INIT_ATTRIBUTE, 'true');

    window.gsap.fromTo(
      zoomTarget,
      {
        scale: 1,
        transformOrigin: 'center center',
        willChange: 'transform',
      },
      {
        scale: 1.12,
        ease: 'none',
        scrollTrigger: {
          trigger: element,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 0.35,
          fastScrollEnd: true,
        },
      }
    );
  });

  window.ScrollTrigger.refresh();
}

function activateGsapAnimations(rootEl = document, isDesignModeEvent = false) {
  if (!registerGsap()) return false;

  if (!gsapInitialized) {
    clearFallbackZoomTimeout();
    clearFallbackState();
    gsapInitialized = true;
    document.documentElement.classList.add(GSAP_ACTIVE_CLASSNAME);
  }

  initializeGsapScrollAnimationTrigger(rootEl, isDesignModeEvent);
  initializeGsapScrollZoomAnimationTrigger(rootEl, isDesignModeEvent);
  return true;
}

function percentageSeen(element) {
  const viewportHeight = window.innerHeight;
  const scrollY = window.scrollY;
  const elementPositionY = element.getBoundingClientRect().top + scrollY;
  const elementHeight = element.offsetHeight;

  if (elementPositionY > scrollY + viewportHeight) return 0;
  if (elementPositionY + elementHeight < scrollY) return 100;

  const distance = scrollY + viewportHeight - elementPositionY;
  const percentage = distance / ((viewportHeight + elementHeight) / 100);
  return Math.round(percentage);
}

window.addEventListener('DOMContentLoaded', () => {
  if (shouldUseGsapEnhancement() && activateGsapAnimations()) return;

  initializeScrollAnimationTriggerFallback();

  if (prefersReducedMotion()) return;

  if (!shouldUseGsapEnhancement()) {
    initializeScrollZoomAnimationTriggerFallback();
    return;
  }

  window.addEventListener(
    'theme:gsap-ready',
    () => {
      if (!activateGsapAnimations() && !fallbackState.zoomInitialized) {
        initializeScrollZoomAnimationTriggerFallback();
      }
    },
    { once: true }
  );

  scheduleFallbackZoomInitialization();
});

if (Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (gsapInitialized && canUseGsap()) {
      activateGsapAnimations(event.target, true);
    } else {
      initializeScrollAnimationTriggerFallback(event.target, true);
    }
  });

  document.addEventListener('shopify:section:reorder', () => {
    if (gsapInitialized && canUseGsap()) {
      activateGsapAnimations(document, true);
    } else {
      initializeScrollAnimationTriggerFallback(document, true);
    }
  });

  document.addEventListener('shopify:section:unload', (event) => {
    if (!gsapInitialized || typeof window.ScrollTrigger === 'undefined') return;

    const sectionRoot = event.target;
    window.ScrollTrigger.getAll().forEach((trigger) => {
      if (trigger.trigger && sectionRoot.contains(trigger.trigger)) {
        trigger.kill();
      }
    });

    sectionRoot.querySelectorAll(`[${GSAP_SCROLL_INIT_ATTRIBUTE}]`).forEach((element) => {
      element.removeAttribute(GSAP_SCROLL_INIT_ATTRIBUTE);
    });

    sectionRoot.querySelectorAll(`[${GSAP_ZOOM_INIT_ATTRIBUTE}]`).forEach((element) => {
      element.removeAttribute(GSAP_ZOOM_INIT_ATTRIBUTE);
    });

    if (window.ScrollTrigger.getAll().length) window.ScrollTrigger.refresh();
  });
}
